import { useState, useEffect, useCallback } from 'react';
import {
  Activity, AlertTriangle, CheckCircle, ChevronRight, Clock,
  Eye, Loader2, RefreshCw, Search, Shield, ShieldAlert, ShieldCheck,
  ShieldQuestion, Video, XCircle, Filter, ArrowUpDown, BookOpen, GraduationCap, Cog, Target, Moon, TrendingUp, HelpCircle
} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import recordingAssuranceTutorial from '../components/tutorials/configs/recordingAssuranceTutorial';
import { useNavigate } from 'react-router-dom';
import {
  RadialBarChart, RadialBar, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import RaLicenseBanner from '../components/RaLicenseBanner';
import '../styles/v2/pages/recording-assurance.css';

const STATUS_CONFIG = {
  IDLE: { color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.12)', icon: Moon, label: 'Idle' },
  LEARNING: { color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.12)', icon: GraduationCap, label: 'Learning' },
  VERIFIED: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.12)', icon: ShieldCheck, label: 'Verified' },
  DEGRADED: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)', icon: ShieldAlert, label: 'Degraded' },
  AT_RISK:  { color: '#f97316', bg: 'rgba(249, 115, 22, 0.12)', icon: AlertTriangle, label: 'At Risk' },
  FAILED:   { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)', icon: XCircle, label: 'Failed' },
  UNKNOWN:  { color: '#64748b', bg: 'rgba(100, 116, 139, 0.12)', icon: ShieldQuestion, label: 'Unknown' },
};

const RecordingAssurance = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [predictions, setPredictions] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('currentConfidence');
  const [sortDir, setSortDir] = useState('asc');

  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(recordingAssuranceTutorial);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/recording-assurance/dashboard');
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('[RA] Dashboard fetch error:', err);
    }
  }, []);

  const fetchCameras = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('sortBy', sortBy);
      params.set('sortDir', sortDir);
      const res = await fetchWithTenant(`/api/recording-assurance/cameras?${params}`);
      if (res.ok) {
        const data = await res.json();
        setCameras(data);
      }
    } catch (err) {
      console.error('[RA] Cameras fetch error:', err);
    }
  }, [statusFilter, sortBy, sortDir]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchDashboard(), fetchCameras()]);
    setRefreshing(false);
  }, [fetchDashboard, fetchCameras]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchCameras()]);
      setLoading(false);
    };
    load();
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard, fetchCameras, refresh]);

  useEffect(() => { fetchCameras(); }, [statusFilter, sortBy, sortDir, fetchCameras]);

  const triggerCheck = async (configId, cameraName) => {
    try {
      toast.info(`Checking ${cameraName}...`);
      const res = await fetchWithTenant(`/api/recording-assurance/cameras/${configId}/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        toast.success(`${cameraName} checked`);
        await refresh();
      } else {
        toast.error('Check failed');
      }
    } catch (err) {
      toast.error('Check failed: ' + err.message);
    }
  };

  const filteredCameras = cameras.filter(c =>
    !searchQuery || c.cameraName?.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => {
    const aIdle = a.currentStatus === 'UNKNOWN' && (a.recordingMode === 'MOTION' || a.recordingMode === 'SCHEDULED');
    const bIdle = b.currentStatus === 'UNKNOWN' && (b.recordingMode === 'MOTION' || b.recordingMode === 'SCHEDULED');
    // Real problems first, then verified, then idle at bottom
    const aScore = aIdle ? 101 : a.currentConfidence;
    const bScore = bIdle ? 101 : b.currentConfidence;
    return aScore - bScore;
  });

  if (loading) {
    return (
      <div className="ra-loading">
        <Loader2 className="spin" size={32} />
        <p>Loading Recording Assurance...</p>
      </div>
    );
  }

  const gaugeData = dashboard ? [{
    name: 'Confidence',
    value: dashboard.avgConfidence,
    fill: dashboard.avgConfidence >= 90 ? '#22c55e' :
          dashboard.avgConfidence >= 70 ? '#f59e0b' :
          dashboard.avgConfidence >= 30 ? '#f97316' : '#ef4444'
  }] : [];

  const overallConfig = dashboard ? STATUS_CONFIG[dashboard.overallStatus] || STATUS_CONFIG.UNKNOWN : STATUS_CONFIG.UNKNOWN;
  const OverallIcon = overallConfig.icon;

  return (
    <div className="ra-page">
      <RaLicenseBanner />
      <div className="ra-header">
        <div className="ra-header-left">
          <Shield size={24} className="ra-header-icon" />
          <div>
            <h1>Recording Assurance</h1>
            <p className="ra-subtitle">Continuous recording verification across all cameras</p>
          </div>
        </div>
        <div className="ra-header-actions">
          <button className="tutorial-help-btn" onClick={() => startTutorial(recordingAssuranceTutorial.module, recordingAssuranceTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
          {dashboard?.service && (
            <span className={`ra-service-badge ${dashboard.service.running ? 'active' : 'inactive'}`}>
              <Activity size={14} />
              {dashboard.service.running ? 'Service Active' : 'Service Stopped'}
            </span>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/recording-assurance/setup')}>
            <Cog size={14} />
            Setup
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/recording-assurance/accuracy')}>
            <Target size={14} />
            Accuracy
          </button>
          <button className="btn btn-secondary btn-sm" onClick={refresh} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="ra-summary-grid">
        {/* Overall Health Gauge */}
        <div className="ra-card ra-gauge-card">
          <div className="ra-gauge-container">
            <ResponsiveContainer width={140} height={140}>
              <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" startAngle={180} endAngle={0} barSize={12} data={gaugeData}>
                <RadialBar background={{ fill: 'rgba(255,255,255,0.05)' }} dataKey="value" cornerRadius={6} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="ra-gauge-label">
              <span className="ra-gauge-value" style={{ color: overallConfig.color }}>
                {dashboard?.avgConfidence ?? 0}%
              </span>
              <span className="ra-gauge-text">confidence</span>
            </div>
          </div>
          <div className="ra-gauge-status" style={{ color: overallConfig.color }}>
            <OverallIcon size={16} />
            {overallConfig.label}
          </div>
        </div>

        {/* Stat Cards */}
        <div className="ra-card ra-stat-card">
          <div className="ra-stat-icon" style={{ background: STATUS_CONFIG.VERIFIED.bg, color: STATUS_CONFIG.VERIFIED.color }}>
            <ShieldCheck size={20} />
          </div>
          <div className="ra-stat-content">
            <span className="ra-stat-value">{dashboard?.cameras?.verified ?? 0}</span>
            <span className="ra-stat-label">Verified</span>
          </div>
        </div>

        <div className="ra-card ra-stat-card">
          <div className="ra-stat-icon" style={{ background: STATUS_CONFIG.DEGRADED.bg, color: STATUS_CONFIG.DEGRADED.color }}>
            <ShieldAlert size={20} />
          </div>
          <div className="ra-stat-content">
            <span className="ra-stat-value">{dashboard?.cameras?.degraded ?? 0}</span>
            <span className="ra-stat-label">Degraded</span>
          </div>
        </div>

        <div className="ra-card ra-stat-card">
          <div className="ra-stat-icon" style={{ background: STATUS_CONFIG.FAILED.bg, color: STATUS_CONFIG.FAILED.color }}>
            <XCircle size={20} />
          </div>
          <div className="ra-stat-content">
            <span className="ra-stat-value">{(dashboard?.cameras?.failed ?? 0) + (dashboard?.cameras?.atRisk ?? 0)}</span>
            <span className="ra-stat-label">Failed / At Risk</span>
          </div>
        </div>

        <div className="ra-card ra-stat-card">
          <div className="ra-stat-icon" style={{ background: STATUS_CONFIG.UNKNOWN.bg, color: STATUS_CONFIG.UNKNOWN.color }}>
            <ShieldQuestion size={20} />
          </div>
          <div className="ra-stat-content">
            <span className="ra-stat-value">{dashboard?.cameras?.unknown ?? 0}</span>
            <span className="ra-stat-label">Unknown / Idle</span>
          </div>
        </div>

        <div className="ra-card ra-stat-card">
          <div className="ra-stat-icon" style={{ background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' }}>
            <AlertTriangle size={20} />
          </div>
          <div className="ra-stat-content">
            <span className="ra-stat-value">{dashboard?.recentGaps24h ?? 0}</span>
            <span className="ra-stat-label">Gaps (24h)</span>
                {dashboard?.gapBreakdown && (
                  <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                    {dashboard.gapBreakdown.CRITICAL > 0 && <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>{dashboard.gapBreakdown.CRITICAL} critical</span>}
                    {dashboard.gapBreakdown.MAJOR > 0 && <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(249,115,22,0.15)', color: '#f97316' }}>{dashboard.gapBreakdown.MAJOR} major</span>}
                    {dashboard.gapBreakdown.MODERATE > 0 && <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>{dashboard.gapBreakdown.MODERATE} mod</span>}
                    {dashboard.gapBreakdown.MINOR > 0 && <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(100,116,139,0.1)', color: 'var(--text-tertiary)' }}>{dashboard.gapBreakdown.MINOR} minor</span>}
                  </div>
                )}
          </div>
        </div>
      </div>

      {/* Learning Mode Banner */}
      {dashboard?.learning?.isLearning && (
        <div className="ra-learning-banner">
          <div className="ra-learning-banner-icon">
            <GraduationCap size={24} />
          </div>
          <div className="ra-learning-banner-content">
            <strong>Learning Mode -- Day {dashboard.learning.dayNumber} of 7</strong>
            <p>
              Recording Assurance is building a behavioral baseline for each camera. During this period, 
              alerts are suppressed and scores may fluctuate as the system learns normal patterns. 
              Baseline analysis will activate automatically after 7 days of data collection.
            </p>
            <div className="ra-learning-progress">
              <div className="ra-learning-progress-bar">
                <div className="ra-learning-progress-fill" style={{ width: dashboard.learning.progress + '%' }} />
              </div>
              <span>{dashboard.learning.progress}% complete -- {dashboard.learning.daysRemaining} days remaining</span>
            </div>
          </div>
        </div>
      )}

            {/* Camera Grid */}
      <div className="ra-card ra-cameras-section">
        <div className="ra-cameras-header">

        {/* Predictions */}
        {predictions?.predictions?.length > 0 && (
          <div className="ra-card" style={{ marginBottom: '20px' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <TrendingUp size={18} style={{ color: '#a855f7' }} />
              <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)' }}>Predicted Events</h3>
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)' }}>Based on {predictions.predictions.length} detected pattern(s)</span>
            </div>
            <div style={{ padding: '12px 20px' }}>
              {predictions.predictions.slice(0, 3).map((pred, i) => (
                <div key={i} style={{ display: 'flex', gap: '14px', padding: '10px 0', borderBottom: i < Math.min(predictions.predictions.length, 3) - 1 ? '1px solid var(--border-color)' : 'none', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: pred.type === 'DEGRADATION_TREND' ? 'rgba(239,68,68,0.12)' : pred.type === 'INFRASTRUCTURE_RECURRING' ? 'rgba(249,115,22,0.12)' : 'rgba(168,85,247,0.12)',
                    color: pred.type === 'DEGRADATION_TREND' ? '#ef4444' : pred.type === 'INFRASTRUCTURE_RECURRING' ? '#f97316' : '#a855f7'
                  }}>
                    <TrendingUp size={16} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '2px' }}>
                      {pred.patternSummary}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      {pred.summary}
                    </div>
                    {pred.timeUntil && (
                      <span style={{ fontSize: '11px', color: '#a855f7', fontWeight: 600, marginTop: '4px', display: 'inline-block' }}>
                        Next expected in {pred.timeUntil}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', padding: '2px 8px', background: 'var(--bg-primary)', borderRadius: '4px' }}>
                    {pred.confidence}% confidence
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}


          <h2>Camera Status</h2>
          <div className="ra-cameras-controls">
            <div className="ra-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Search cameras..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="ra-filter-group">
              <Filter size={14} />
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="all">All Status</option>
                <option value="VERIFIED">Verified</option>
                <option value="DEGRADED">Degraded</option>
                <option value="AT_RISK">At Risk</option>
                <option value="FAILED">Failed</option>
                <option value="UNKNOWN">Unknown</option>
              </select>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => {
              setSortDir(d => d === 'asc' ? 'desc' : 'asc');
            }}>
              <ArrowUpDown size={14} />
              {sortDir === 'asc' ? 'Worst First' : 'Best First'}
            </button>
          </div>
        </div>

        <div className="ra-cameras-grid">
          {filteredCameras.length === 0 ? (
            <div className="ra-empty">
              <Video size={32} />
              <p>No cameras match your filters</p>
            </div>
          ) : (
            filteredCameras.map(cam => {
              const isMotionIdle = cam.currentStatus === 'UNKNOWN' && (cam.recordingMode === 'MOTION' || cam.recordingMode === 'SCHEDULED');
              const config = isMotionIdle ? STATUS_CONFIG.IDLE : (STATUS_CONFIG[cam.currentStatus] || STATUS_CONFIG.UNKNOWN);
              const StatusIcon = config.icon;
              return (
                <div key={cam.id} className="ra-camera-card" style={{ borderLeftColor: config.color }} onClick={() => navigate(`/recording-assurance/${cam.id}`)}>
                  <div className="ra-camera-header">
                    <div className="ra-camera-status-dot" style={{ background: config.color }} />
                    <span className="ra-camera-name">{cam.cameraName || cam.cameraId}</span>
                    <span className="ra-camera-confidence" style={{ color: config.color }}>
                      {isMotionIdle ? '--' : cam.currentConfidence + '%'}
                    </span>
                  </div>
                  <div className="ra-camera-details">
                    <span className="ra-camera-tag">
                      <StatusIcon size={12} />
                      {config.label}
                    </span>
                    <span className="ra-camera-tag">
                      <Video size={12} />
                      {cam.recordingMode}
                    </span>
                    {cam.lastCheckedAt && (
                      <span className="ra-camera-tag">
                        <Clock size={12} />
                        {new Date(cam.lastCheckedAt).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                  <div className="ra-camera-actions">
                    <button className="btn btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); triggerCheck(cam.id, cam.cameraName); }}>
                      <RefreshCw size={12} /> Check Now
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showPrompt && (
        <TutorialPrompt
          module={recordingAssuranceTutorial.module}
          moduleName={recordingAssuranceTutorial.moduleName}
          steps={recordingAssuranceTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default RecordingAssurance;
